import { request } from "./api";
import { db } from "./db";
import type { Backend, Project, UploadRecord } from "./types";

const MAX_SIZE = 4 * 1024 ** 3;

async function sha256(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function uploadVideo(
  backend: Backend,
  file: File,
  onProgress: (progress: number, label: string) => void,
): Promise<Project> {
  if (file.size > MAX_SIZE) throw new Error("Video je větší než limit 4 GB.");
  const project = await request<Project>(backend, "/api/v1/projects", {
    method: "POST",
    body: JSON.stringify({ filename: file.name, size: file.size, backend: backend.kind }),
  });
  const started = await request<{ upload_id: string; chunk_size: number; received_parts: number[] }>(
    backend,
    `/api/v1/projects/${project.project_id}/upload/start`,
    { method: "POST", body: JSON.stringify({ revision: project.revision }) },
  );
  const key = `${backend.kind}:${project.project_id}`;
  const record: UploadRecord = {
    key,
    backend: backend.kind,
    baseUrl: backend.baseUrl,
    projectId: project.project_id,
    uploadId: started.upload_id,
    filename: file.name,
    size: file.size,
    lastModified: file.lastModified,
    receivedParts: started.received_parts || [],
    chunkSize: started.chunk_size,
  };
  await db.putUpload(record);
  await db.setState("activeProject", { backend: backend.kind, baseUrl: backend.baseUrl, projectId: project.project_id });
  return resumeUpload(backend, file, project, record, onProgress);
}

export async function resumeUpload(
  backend: Backend,
  file: File,
  project: Project,
  record: UploadRecord,
  onProgress: (progress: number, label: string) => void,
): Promise<Project> {
  if (file.name !== record.filename || file.size !== record.size || file.lastModified !== record.lastModified) {
    throw new Error("Pro pokračování vyber stejné video jako předtím.");
  }
  const status = await request<{ received_parts: number[] }>(
    backend,
    `/api/v1/projects/${project.project_id}/upload/${record.uploadId}`,
  );
  const received = new Set(status.received_parts);
  const count = Math.ceil(file.size / record.chunkSize);
  const pending = Array.from({ length: count }, (_, index) => index + 1).filter((part) => !received.has(part));
  let done = count - pending.length;

  async function worker(): Promise<void> {
    while (pending.length) {
      const part = pending.shift();
      if (!part) return;
      const start = (part - 1) * record.chunkSize;
      const chunk = file.slice(start, Math.min(file.size, start + record.chunkSize));
      const checksum = await sha256(chunk);
      await request(
        backend,
        `/api/v1/projects/${project.project_id}/upload/${record.uploadId}/parts/${part}`,
        { method: "PUT", body: chunk, headers: { "X-Chunk-SHA256": checksum, "Content-Type": "application/octet-stream" } },
      );
      received.add(part);
      done += 1;
      record.receivedParts = [...received].sort((a, b) => a - b);
      await db.putUpload(record);
      onProgress(done / count, `Nahrávám část ${done} z ${count}`);
    }
  }

  await Promise.all(Array.from({ length: Math.min(3, pending.length || 1) }, () => worker()));
  onProgress(1, "Kontroluju video…");
  const completed = await request<Project>(
    backend,
    `/api/v1/projects/${project.project_id}/upload/${record.uploadId}/complete`,
    { method: "POST", body: JSON.stringify({ revision: project.revision, idempotency_key: `ingest:${project.project_id}:1` }) },
  );
  await db.deleteUpload(record.key);
  return completed;
}
